import { OrderedSet } from '../../../mol-data/int';
import { Vec3 } from '../../../mol-math/linear-algebra';
import { PrincipalAxes } from '../../../mol-math/linear-algebra/matrix/principal-axes';
import { radToDeg } from '../../../mol-math/misc';
import { ElementIndex, ResidueIndex, StructureElement, StructureProperties, Unit, UnitRing } from '../../../mol-model/structure';
import { AccessibleSurfaceAreaProvider } from '../../../mol-model-props/computed/accessible-surface-area';
import { AccessibleSurfaceArea } from '../../../mol-model-props/computed/accessible-surface-area/shrake-rupley';
import { geometryLabel } from '../../../mol-model-props/computed/chemistry/geometry';
import { ValenceModelProvider } from '../../../mol-model-props/computed/valence-model';
import { PluginContext } from '../../../mol-plugin/context';
import { Task } from '../../../mol-task';
import { AlignSequences } from '../../../mol-model/sequence/alignment/sequence';
import { residueFromLocation } from '../identity';
import { ExpandResultCache, failure, pagedResult, success, toMcpResult } from '../result';
import { createSelectorResolverContext, parseSelector, resolveSelector, selectorPropertiesRequestSchema, selectorRequestSchema } from '../selector';
import { ToolDefinition, ToolSchemaObject, WebMcpRequest } from '../registration';
import { Selector } from '../types';

interface TargetRequest { readonly target: Selector }
interface PairRequest { readonly a: Selector, readonly b: Selector }
type CoordinationSiteItem = ReturnType<typeof atomFields> & { readonly coordinationNumber: number, readonly ligandAtomIds: readonly string[], readonly ligandResidueIds: readonly string[], readonly ligandDistances: readonly number[] }
type RingSystemItem = { readonly ref: string, readonly unitId: number, readonly ringIndex: number, readonly aromatic: boolean, readonly fingerprint: string, readonly fusedComponentIndex: number, readonly fusedRingIndices: readonly number[], readonly atomIds: readonly string[] }
type GlycanItem = ReturnType<typeof residueFields> & { readonly component: string, readonly ringAtomCount: number, readonly linkedGlycanResidueIds: readonly string[], readonly terminalAttachmentAtomIds: readonly string[] }
type AlignmentPair = { readonly aResidueId: string, readonly bResidueId: string }
type BackboneDihedralItem = ReturnType<typeof residueFields> & { readonly hasPhi: boolean, readonly phiDegrees: number, readonly hasPsi: boolean, readonly psiDegrees: number, readonly hasOmega: boolean, readonly omegaDegrees: number }
export type SasaItem = ReturnType<typeof residueFields> & { readonly area: number, readonly accessibility: string }
type SasaChainAccumulator = { ref: string, chainAuth: string, chainLabel: string, residueCount: number, totalArea: number, buriedCount: number, exposedCount: number }
type ValenceItem = ReturnType<typeof atomFields> & { readonly charge: number, readonly implicitHydrogens: number, readonly totalHydrogens: number, readonly geometry: string }
const PairSchema: ToolSchemaObject = selectorPropertiesRequestSchema(['a', 'b']);

function parseTargetRequest(input: WebMcpRequest): TargetRequest | undefined {
    const target = parseSelector(input.target);
    return target === void 0 ? void 0 : { target };
}

function parsePairRequest(input: WebMcpRequest): PairRequest | undefined {
    const a = parseSelector(input.a);
    const b = parseSelector(input.b);
    return a === void 0 || b === void 0 ? void 0 : { a, b };
}

function invalidInput() {
    return toMcpResult(failure('Invalid tool input.', 'Provide selectors matching the published input schema.'));
}

function resolveTarget(plugin: PluginContext, request: TargetRequest) {
    return resolveSelector(request.target, createSelectorResolverContext(plugin));
}

function residueFields(ref: string, structure: StructureElement.Loci['structure'], unit: Unit.Atomic, element: ElementIndex) {
    const location = StructureElement.Location.create(structure, unit, element);
    const residue = residueFromLocation(ref, location);
    if (!residue) throw new Error('Expected an atomic residue location.');
    return { residueId: residue.id, ...residue, insCode: residue.insCode ?? '' };
}

function atomFields(ref: string, structure: StructureElement.Loci['structure'], unit: Unit.Atomic, element: ElementIndex) {
    const residue = residueFields(ref, structure, unit, element);
    const location = StructureElement.Location.create(structure, unit, element);
    const position = unit.conformation.position(element, Vec3());
    const atomName = StructureProperties.atom.label_atom_id(location);
    const altId = StructureProperties.atom.label_alt_id(location) || '';
    return { ...residue, atomId: `${residue.residueId}/${atomName}`, atomName, element: StructureProperties.atom.type_symbol(location), altId, x: position[0], y: position[1], z: position[2] };
}

function selectedAtoms(loci: StructureElement.Loci) {
    const atoms = new Map<number, Set<ElementIndex>>();
    for (const entry of loci.elements) {
        const unit = entry.unit;
        if (!Unit.isAtomic(unit)) continue;
        let selected = atoms.get(unit.id);
        if (selected === void 0) atoms.set(unit.id, selected = new Set<ElementIndex>());
        OrderedSet.forEach(entry.indices, index => selected!.add(unit.elements[index]));
    }
    return atoms;
}

function selectedResidues(loci: StructureElement.Loci) {
    const residues = new Map<number, Set<ResidueIndex>>();
    for (const entry of loci.elements) {
        const unit = entry.unit;
        if (!Unit.isAtomic(unit)) continue;
        let selected = residues.get(unit.id);
        if (selected === void 0) residues.set(unit.id, selected = new Set<ResidueIndex>());
        OrderedSet.forEach(entry.indices, index => selected!.add(unit.getResidueIndex(index)));
    }
    return residues;
}

function residueCount(entry: StructureElement.Loci.Element) {
    const residues = new Set<ResidueIndex>();
    const unit = entry.unit;
    if (!Unit.isAtomic(unit)) return 0;
    OrderedSet.forEach(entry.indices, index => residues.add(unit.getResidueIndex(index)));
    return residues.size;
}

function normalizedAxis(vector: Vec3) {
    const magnitude = Vec3.magnitude(vector);
    return magnitude === 0 ? [0, 0, 0] : [vector[0] / magnitude, vector[1] / magnitude, vector[2] / magnitude];
}

function selectedPositions(loci: StructureElement.Loci) {
    const positions: number[] = [];
    for (const entry of loci.elements) {
        const unit = entry.unit;
        if (!Unit.isAtomic(unit)) continue;
        OrderedSet.forEach(entry.indices, index => {
            const position = unit.conformation.position(unit.elements[index], Vec3());
            positions.push(position[0], position[1], position[2]);
        });
    }
    return positions;
}

function positionsForAtoms(unit: Unit.Atomic, residueIndex: ResidueIndex, atomNames: readonly string[]) {
    const index = unit.model.atomicHierarchy.index;
    const positions: Vec3[] = [];
    for (const atomName of atomNames) {
        const element = index.findAtomOnResidue(residueIndex, atomName);
        if (element === -1) return void 0;
        positions.push(unit.conformation.position(element, Vec3()));
    }
    return positions;
}

function peptideNeighbor(unit: Unit.Atomic, residueIndex: ResidueIndex, direction: -1 | 1, atomNames: readonly string[]) {
    const offsets = unit.model.atomicHierarchy.residueAtomSegments.offsets;
    const currentElement = offsets[residueIndex];
    const neighborElement = offsets[residueIndex + direction];
    if (neighborElement === void 0 || unit.chainIndex[currentElement] !== unit.chainIndex[neighborElement]) return void 0;
    const neighborIndex = unit.residueIndex[neighborElement];
    const current = positionsForAtoms(unit, residueIndex, direction === -1 ? ['N'] : ['C']);
    const neighbor = positionsForAtoms(unit, neighborIndex, direction === -1 ? ['C'] : ['N']);
    if (!current || !neighbor || Vec3.distance(current[0], neighbor[0]) > 2) return void 0;
    return positionsForAtoms(unit, neighborIndex, atomNames);
}

export function backboneDihedralItems(ref: string, loci: StructureElement.Loci): readonly BackboneDihedralItem[] {
    const selected = selectedResidues(loci);
    const values: BackboneDihedralItem[] = [];
    for (const unit of loci.structure.units) {
        if (!Unit.isAtomic(unit)) continue;
        for (const residueIndex of selected.get(unit.id) ?? []) {
            const current = positionsForAtoms(unit, residueIndex, ['N', 'CA', 'C']);
            if (!current) continue;
            const previous = peptideNeighbor(unit, residueIndex, -1, ['CA', 'C']);
            const next = peptideNeighbor(unit, residueIndex, 1, ['N']);
            const phiDegrees = previous ? radToDeg(Vec3.dihedralAngle(previous[1], current[0], current[1], current[2])) : 0;
            const omegaDegrees = previous ? radToDeg(Vec3.dihedralAngle(previous[0], previous[1], current[0], current[1])) : 0;
            const psiDegrees = next ? radToDeg(Vec3.dihedralAngle(current[0], current[1], current[2], next[0])) : 0;
            values.push({ ...residueFields(ref, loci.structure, unit, unit.model.atomicHierarchy.residueAtomSegments.offsets[residueIndex]), hasPhi: previous !== void 0, phiDegrees, hasPsi: next !== void 0, psiDegrees, hasOmega: previous !== void 0, omegaDegrees });
        }
    }
    return values;
}

export function sasaItems(ref: string, loci: StructureElement.Loci, value: AccessibleSurfaceArea): readonly SasaItem[] {
    const residues = new Map<string, SasaItem>();
    StructureElement.Loci.forEachLocation(loci, location => {
        if (!Unit.isAtomic(location.unit)) return;
        const item = residueFields(ref, loci.structure, location.unit, location.element);
        if (residues.has(item.residueId)) return;
        const area = AccessibleSurfaceArea.getValue(location, value);
        if (area < 0) return;
        const flag = AccessibleSurfaceArea.getFlag(location, value);
        const accessibility = flag === AccessibleSurfaceArea.Flags.Buried ? 'buried' : flag === AccessibleSurfaceArea.Flags.Accessible ? 'exposed' : 'not-classified';
        residues.set(item.residueId, { ...item, area, accessibility });
    });
    return [...residues.values()];
}

export function sasaSummaryResult(records: readonly SasaItem[], expandResults: ExpandResultCache) {
    const totalArea = records.reduce((sum, item) => sum + item.area, 0);
    const chains: SasaChainAccumulator[] = [];
    let buriedCount = 0;
    let exposedCount = 0;
    for (const record of records) {
        if (record.accessibility === 'buried') buriedCount++;
        if (record.accessibility === 'exposed') exposedCount++;
        let chain = chains.find(item => item.ref === record.ref && item.chainAuth === record.chainAuth && item.chainLabel === record.chainLabel);
        if (chain === void 0) {
            chain = { ref: record.ref, chainAuth: record.chainAuth, chainLabel: record.chainLabel, residueCount: 0, totalArea: 0, buriedCount: 0, exposedCount: 0 };
            chains.push(chain);
        }
        chain.residueCount++;
        chain.totalArea += record.area;
        if (record.accessibility === 'buried') chain.buriedCount++;
        if (record.accessibility === 'exposed') chain.exposedCount++;
    }
    const topExposedResidues = records.filter(item => item.accessibility === 'exposed').sort((a, b) => b.area - a.area).slice(0, 2);
    return pagedResult(expandResults, chains, page => success(`Calculated ${totalArea.toFixed(2)} Å² across ${records.length} residues.`, { type: 'sasa-summary', totalArea, buriedCount, exposedCount, chainCount: chains.length, chains: page.items, topExposedResidues, topExposedLimit: 2, residueCount: records.length }, page.truncation));
}

async function ensureSasa(plugin: PluginContext, structure: StructureElement.Loci['structure']) {
    if (AccessibleSurfaceAreaProvider.get(structure).value !== void 0) return;
    await plugin.runTask(Task.create('WebMCP accessible surface area', runtime => AccessibleSurfaceAreaProvider.attach({ runtime, assetManager: plugin.managers.asset, errorContext: plugin.errorContext }, structure)));
}

async function ensureValence(plugin: PluginContext, structure: StructureElement.Loci['structure']) {
    if (ValenceModelProvider.get(structure).value !== void 0) return;
    await plugin.runTask(Task.create('WebMCP valence model', runtime => ValenceModelProvider.attach({ runtime, assetManager: plugin.managers.asset, errorContext: plugin.errorContext }, structure)));
}

/** Read-only scientific structure tools using Mol* calculations and the shared selector resolver. */
export function createStructureScienceTools(plugin: PluginContext, expandResults: ExpandResultCache): readonly ToolDefinition[] {
    return [
        {
            name: 'get-glycans', description: 'Return selected glycan residues and glycosidic links calculated by Mol*.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                const records = matched.entries.flatMap(entry => {
                    const selected = selectedResidues(entry.loci);
                    const carbohydrates = entry.structure.carbohydrates;
                    const residueFor = (index: number) => {
                        const item = carbohydrates.elements[index];
                        return residueFields(entry.ref, entry.structure, item.unit, item.unit.model.atomicHierarchy.residueAtomSegments.offsets[item.residueIndex]);
                    };
                    const glycans: GlycanItem[] = [];
                    for (let index = 0; index < carbohydrates.elements.length; index++) {
                        const item = carbohydrates.elements[index];
                        if (!selected.get(item.unit.id)?.has(item.residueIndex)) continue;
                        const linkedGlycanResidueIds = new Set<string>();
                        for (const link of carbohydrates.links) {
                            if (link.carbohydrateIndexA === index) linkedGlycanResidueIds.add(residueFor(link.carbohydrateIndexB).residueId);
                            if (link.carbohydrateIndexB === index) linkedGlycanResidueIds.add(residueFor(link.carbohydrateIndexA).residueId);
                        }
                        const terminalAttachmentAtomIds: string[] = [];
                        for (const link of carbohydrates.terminalLinks) if (link.carbohydrateIndex === index) terminalAttachmentAtomIds.push(atomFields(entry.ref, entry.structure, link.elementUnit, link.elementUnit.elements[link.elementIndex]).atomId);
                        glycans.push({ ...residueFor(index), component: item.component.name, ringAtomCount: item.unit.rings.all[item.ringIndex].length, linkedGlycanResidueIds: Array.from(linkedGlycanResidueIds), terminalAttachmentAtomIds });
                    }
                    return glycans;
                });
                return pagedResult(expandResults, records, page => success(`Found ${records.length} glycan residues.`, { type: 'glycans', glycans: page.items, total: records.length }, page.truncation));
            }
        },
        {
            name: 'get-coordination-sites', description: 'Return selected coordination sites and their bonded ligands calculated by Mol*.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                const records = matched.entries.flatMap(entry => {
                    const selected = selectedAtoms(entry.loci);
                    const coordination = entry.structure.coordination;
                    const sites: CoordinationSiteItem[] = [];
                    for (let i = 0; i < coordination.sites.count; i++) {
                        const unit = entry.structure.unitMap.get(coordination.sites.unitIds[i]);
                        if (!unit || !Unit.isAtomic(unit)) continue;
                        const element = unit.elements[coordination.sites.indices[i]];
                        if (!selected.get(unit.id)?.has(element)) continue;
                        const ligandAtomIds: string[] = [];
                        const ligandResidueIds: string[] = [];
                        const ligandDistances: number[] = [];
                        const sitePosition = unit.conformation.position(element, Vec3());
                        const siteIndex = coordination.getSiteIndex(unit, element);
                        if (siteIndex === -1) continue;
                        coordination.eachLigand(siteIndex, location => {
                            if (!Unit.isAtomic(location.unit)) return;
                            const ligand = atomFields(entry.ref, entry.structure, location.unit, location.element);
                            ligandAtomIds.push(ligand.atomId);
                            ligandResidueIds.push(ligand.residueId);
                            ligandDistances.push(Vec3.distance(sitePosition, location.unit.conformation.position(location.element, Vec3())));
                        });
                        sites.push({ ...atomFields(entry.ref, entry.structure, unit, element), coordinationNumber: coordination.sites.numbers[i], ligandAtomIds, ligandResidueIds, ligandDistances });
                    }
                    return sites;
                });
                return pagedResult(expandResults, records, page => success(`Found ${records.length} coordination sites.`, { type: 'coordination-sites', sites: page.items, total: records.length }, page.truncation));
            }
        },
        {
            name: 'get-principal-axes', description: 'Calculate principal axes from selected atomic coordinates.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                const axes = matched.entries.flatMap(entry => {
                    const positions = selectedPositions(entry.loci);
                    if (positions.length < 6 || positions.some(position => !Number.isFinite(position))) return [];
                    const value = PrincipalAxes.ofPositions(positions);
                    const momentMagnitudes = [Vec3.magnitude(value.momentsAxes.dirA), Vec3.magnitude(value.momentsAxes.dirB), Vec3.magnitude(value.momentsAxes.dirC)];
                    const boxMagnitudes = [Vec3.magnitude(value.boxAxes.dirA), Vec3.magnitude(value.boxAxes.dirB), Vec3.magnitude(value.boxAxes.dirC)];
                    const smallestMoment = Math.min(...momentMagnitudes);
                    const largestMoment = Math.max(...momentMagnitudes);
                    if (!Number.isFinite(smallestMoment) || !Number.isFinite(largestMoment) || largestMoment === 0) return [];
                    return [{

                        ref: entry.ref,
                        atomCount: positions.length / 3,
                        center: [...value.momentsAxes.origin],
                        principalVectors: { a: normalizedAxis(value.momentsAxes.dirA), b: normalizedAxis(value.momentsAxes.dirB), c: normalizedAxis(value.momentsAxes.dirC) },
                        momentMagnitudes,
                        principalMoments: momentMagnitudes.map(magnitude => magnitude * magnitude),
                        dimensions: boxMagnitudes.map(magnitude => magnitude * 2),
                        boxHalfDimensions: boxMagnitudes,
                        anisotropy: smallestMoment === 0 ? 0 : largestMoment / smallestMoment,
                    }];
                });
                return axes.length ? toMcpResult(success(`Calculated principal axes for ${axes.length} structures.`, { type: 'principal-axes', axes })) : toMcpResult(failure('The target has no atomic coordinates.', 'Select atoms from a loaded atomic structure.'));
            }
        },
        {
            name: 'get-ring-systems', description: 'Return selected Mol* ring systems and atom identities.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                const records = matched.entries.flatMap(entry => {
                    const selected = selectedAtoms(entry.loci);
                    const rings: RingSystemItem[] = [];
                    for (const unit of entry.structure.units) {
                        if (!Unit.isAtomic(unit)) continue;
                        for (let index = 0; index < unit.rings.all.length; index++) {
                            const ring = unit.rings.all[index];
                            let matchesTarget = false;
                            OrderedSet.forEach(ring, unitIndex => {
                                if (selected.get(unit.id)?.has(unit.elements[unitIndex])) matchesTarget = true;
                            });
                            if (!matchesTarget) continue;
                            const atomIds: string[] = [];
                            OrderedSet.forEach(ring, unitIndex => atomIds.push(atomFields(entry.ref, entry.structure, unit, unit.elements[unitIndex]).atomId));
                            const fusedComponentIndex = unit.rings.ringComponentIndex[index];
                            const fusedRingIndices: number[] = [];
                            const fusedRings = unit.rings.ringComponents[fusedComponentIndex];
                            for (let fusedIndex = 0; fusedIndex < fusedRings.length; fusedIndex++) fusedRingIndices.push(fusedRings[fusedIndex]);
                            rings.push({ ref: entry.ref, unitId: unit.id, ringIndex: index, aromatic: UnitRing.isAromatic(unit, ring), fingerprint: UnitRing.fingerprint(unit, ring), fusedComponentIndex, fusedRingIndices, atomIds });
                        }
                    }
                    return rings;
                });
                return pagedResult(expandResults, records, page => success(`Found ${records.length} ring systems.`, { type: 'ring-systems', rings: page.items, total: records.length }, page.truncation));
            }
        },
        {
            name: 'align-sequences', description: 'Align exactly two selected atomic structure regions without changing coordinates.', inputSchema: PairSchema, annotations: { readOnlyHint: true }, execute: input => {
                const request = parsePairRequest(input); if (!request) return invalidInput();
                const a = resolveSelector(request.a, createSelectorResolverContext(plugin)); if (!a.ok) return toMcpResult(a);
                const b = resolveSelector(request.b, createSelectorResolverContext(plugin)); if (!b.ok) return toMcpResult(b);
                const first = a.entries.find(entry => !StructureElement.Loci.isEmpty(entry.loci));
                const second = b.entries.find(entry => !StructureElement.Loci.isEmpty(entry.loci));
                if (!first || !second || first.loci.elements.length !== 1 || second.loci.elements.length !== 1) return toMcpResult(failure('Sequence alignment requires one atomic unit in each target.', 'Target two individual polymer chains or residues.'));
                if (!Unit.isAtomic(first.loci.elements[0].unit) || !Unit.isAtomic(second.loci.elements[0].unit)) return toMcpResult(failure('Sequence alignment requires atomic polymer targets.', 'Coarse-grained units do not retain residue correspondence data.'));
                try {
                    const alignment = AlignSequences.compute({ a: first.loci.elements[0], b: second.loci.elements[0] });
                    const pairs: AlignmentPair[] = [];
                    const seenPairs = new Set<string>();
                    const pairCount = Math.min(OrderedSet.size(alignment.a.indices), OrderedSet.size(alignment.b.indices));
                    let identicalResidueCount = 0;
                    for (let index = 0; index < pairCount; index++) {
                        const aElement = alignment.a.unit.elements[OrderedSet.getAt(alignment.a.indices, index)];
                        const bElement = alignment.b.unit.elements[OrderedSet.getAt(alignment.b.indices, index)];
                        if (!Unit.isAtomic(alignment.a.unit) || !Unit.isAtomic(alignment.b.unit)) continue;
                        const aResidue = residueFields(first.ref, first.structure, alignment.a.unit, aElement);
                        const bResidue = residueFields(second.ref, second.structure, alignment.b.unit, bElement);
                        const pairKey = `${aResidue.residueId}|${bResidue.residueId}`;
                        if (seenPairs.has(pairKey)) continue;
                        seenPairs.add(pairKey);
                        if (aResidue.comp === bResidue.comp) identicalResidueCount++;
                        pairs.push({ aResidueId: aResidue.residueId, bResidueId: bResidue.residueId });
                    }
                    const aResidueCount = residueCount(first.loci.elements[0]);
                    const bResidueCount = residueCount(second.loci.elements[0]);
                    const identityPercent = pairs.length === 0 ? 0 : identicalResidueCount * 100 / pairs.length;
                    return pagedResult(expandResults, pairs, page => success(`Aligned ${pairs.length} residue pairs.`, { type: 'sequence-alignment', score: alignment.score, a: first.ref, b: second.ref, alignedResidueCount: pairs.length, identityPercent, gapsInA: Math.max(0, bResidueCount - pairs.length), gapsInB: Math.max(0, aResidueCount - pairs.length), correspondences: page.items, total: pairs.length }, page.truncation));
                } catch (error) {
                    return toMcpResult(failure('Sequence alignment failed.', error instanceof Error ? error.message : 'Select two polymer chains with sequence data.'));
                }
            }
        },
        {
            name: 'get-backbone-dihedrals', description: 'Calculate phi, psi, and omega directly from selected backbone atom coordinates.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                const records = matched.entries.flatMap(entry => backboneDihedralItems(entry.ref, entry.loci));
                return pagedResult(expandResults, records, page => success(`Calculated backbone dihedrals for ${records.length} residues.`, { type: 'backbone-dihedrals', residues: page.items, total: records.length }, page.truncation));
            }
        },
        {
            name: 'get-sasa-summary', description: 'Compute accessible surface area for selected residues using Mol* Shrake-Rupley.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: async input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                try {
                    for (const entry of matched.entries) await ensureSasa(plugin, entry.structure);
                    const records = matched.entries.flatMap(entry => {
                        const value = AccessibleSurfaceAreaProvider.get(entry.structure).value;
                        return value ? sasaItems(entry.ref, entry.loci, value) : [];
                    });
                    return sasaSummaryResult(records, expandResults);
                } catch (error) {
                    return toMcpResult(failure('Accessible surface area calculation failed.', error instanceof Error ? error.message : 'Select a valid atomic structure and retry.'));
                }
            }
        },
        {
            name: 'get-valence-summary', description: 'Compute Mol* valence-model summaries for selected atoms.', inputSchema: selectorRequestSchema('target'), annotations: { readOnlyHint: true }, execute: async input => {
                const request = parseTargetRequest(input); if (!request) return invalidInput();
                const matched = resolveTarget(plugin, request); if (!matched.ok) return toMcpResult(matched);
                try {
                    for (const entry of matched.entries) await ensureValence(plugin, entry.structure);
                    const records = matched.entries.flatMap(entry => {
                        const value = ValenceModelProvider.get(entry.structure).value;
                        if (!value) return [];
                        const atoms: ValenceItem[] = [];
                        for (const lociEntry of entry.loci.elements) {
                            const unit = lociEntry.unit;
                            if (!Unit.isAtomic(unit)) continue;
                            const model = value.get(unit.id); if (!model) continue;
                            OrderedSet.forEach(lociEntry.indices, index => atoms.push({ ...atomFields(entry.ref, entry.structure, unit, unit.elements[index]), charge: model.charge[index], implicitHydrogens: model.implicitH[index], totalHydrogens: model.totalH[index], geometry: geometryLabel(model.idealGeometry[index]) }));
                        }
                        return atoms;
                    });
                    let netCharge = 0;
                    let chargedAtomCount = 0;
                    let protonatedAtomCount = 0;
                    const geometryCensus: { geometry: string, atomCount: number }[] = [];
                    for (const record of records) {
                        netCharge += record.charge;
                        if (record.charge !== 0) chargedAtomCount++;
                        if (record.totalHydrogens > 0) protonatedAtomCount++;
                        let geometry = geometryCensus.find(item => item.geometry === record.geometry);
                        if (geometry === void 0) {
                            geometry = { geometry: record.geometry, atomCount: 0 };
                            geometryCensus.push(geometry);
                        }
                        geometry.atomCount++;
                    }
                    return pagedResult(expandResults, records, page => success(`Computed valence summaries for ${records.length} atoms.`, { type: 'valence-summary', netCharge, chargedAtomCount, protonatedAtomCount, geometryCensus, atoms: page.items, total: records.length }, page.truncation));
                } catch (error) {
                    return toMcpResult(failure('Valence-model calculation failed.', error instanceof Error ? error.message : 'Select a valid atomic structure and retry.'));
                }
            }
        },
    ];
}
